import { type ReactNode, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { BillingInterval } from '@scio3/shared-types';
import { Loader2, Minus, Plus, ShoppingCart } from 'lucide-react';
import { Button, Input, Separator, RadioGroup, RadioGroupItem, Label, cn } from '@scio3/ui';
import type { UpcomingSubscriptionReviewData } from '@scio3/graphql-client';
import { StripeCardForm } from '../../components/StripeCardForm';
import { getPlanDisplayName, formatCurrencyAmount } from '../../utils/plan-helpers';
import { BILLING_INTERVAL_LABELS } from '../../utils/billing-constants';

/** Controlled numeric input that allows free typing, validates on blur */
function ScreenSpinInput({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const [localValue, setLocalValue] = useState(String(value));
  useEffect(() => {
    setLocalValue(String(value));
  }, [value]);

  // Raw <input> is intentional here — this is a composite spin control fused between
  // two buttons. shadcn <Input> adds its own border/ring/padding that breaks the flush layout.
  return (
    <input
      type="text"
      inputMode="numeric"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value.replace(/\D/g, ''))}
      onBlur={() => {
        const num = parseInt(localValue, 10);
        if (isNaN(num) || num < min) {
          onChange(min);
          setLocalValue(String(min));
        } else if (num > max) {
          onChange(max);
          setLocalValue(String(max));
        } else {
          onChange(num);
          setLocalValue(String(num));
        }
      }}
      className="w-12 text-center text-sm font-medium border-y border-border h-8 bg-primary text-white outline-none"
      aria-label="Number of screens"
    />
  );
}

type Props = {
  isTrial: boolean;
  existingCard: { brand: string; last4: string } | null;
  useExistingCard: boolean;
  onUseExistingCardChange: (value: boolean) => void;
  stripeLoading: boolean;
  stripeClientSecret: string | null;
  stripePaymentMethodOrder: string[];
  /** Return URL for Stripe redirect (encodes charge params for iDEAL/SEPA/Bancontact) */
  stripeReturnUrl?: string;
  defaultEmail: string;
  onStripeSuccess: (id: string) => void;
  isSubmitting: boolean;
  billingInterval: BillingInterval;
  currentPricePerScreen: number;
  screenCount: number;
  onScreenCountChange: (value: number) => void;
  minScreens: number;
  /** Original screen count before changes (limitation.maxQuantity) */
  originalScreenCount: number;
  /** Disable screen count editing (e.g. in upgrade mode where only plan changes) */
  disableScreenCount?: boolean;
  /** Addon total price matching scio1's getSubtotalPrice addon calculation */
  addonTotal: number;
  /** Individual addon line items for Summary display */
  addonItems: Array<{
    key: string;
    name: string;
    pricePerUnit: number;
    quantity: number;
    total: number;
  }>;
  currency: string;
  selectedPlan: string;
  /** Plan interval name from plans[currency][interval].name, e.g. "Yearly" */
  planEditName?: string;
  /** Plan discount percentage from plans[currency][interval].discount, e.g. 10 */
  planEditDiscount?: number;
  reviewResult: UpcomingSubscriptionReviewData | null;
  reviewLoading: boolean;
  /** Credit from getUpcomingSubscription (matching scio1's creditPrice from initFinishPayment) */
  initialCredit: number;
  onConfirmExistingCard: () => void;
  promoCode: string | null;
  onPromoCodeChange: (value: string | null) => void;
  showPromoCode: boolean;
  onShowPromoCode: () => void;
  /** Called when billing address changes in AddressElement — for redirect flow address capture */
  onBillingAddressChange?: (address: Record<string, string> | null) => void;
  /** Called when Stripe form starts processing — for parent loading state */
  onStripeSubmitStart?: () => void;
};

export function WizardStepConfirmation({
  isTrial,
  existingCard,
  useExistingCard,
  onUseExistingCardChange,
  stripeLoading,
  stripeClientSecret,
  stripePaymentMethodOrder,
  stripeReturnUrl,
  defaultEmail,
  onStripeSuccess,
  isSubmitting,
  billingInterval,
  currentPricePerScreen,
  screenCount,
  onScreenCountChange,
  minScreens,
  originalScreenCount,
  disableScreenCount,
  addonTotal,
  addonItems,
  currency,
  selectedPlan,
  planEditName,
  planEditDiscount,
  reviewResult,
  reviewLoading,
  initialCredit,
  onConfirmExistingCard,
  promoCode,
  onPromoCodeChange,
  showPromoCode,
  onShowPromoCode,
  onBillingAddressChange,
  onStripeSubmitStart,
}: Props) {
  const { t } = useTranslation();
  const [stripeProcessing, setStripeProcessing] = useState(false);
  const handleSubmitStart = useCallback(() => {
    setStripeProcessing(true);
    onStripeSubmitStart?.();
  }, [onStripeSubmitStart]);

  // Reset stripeProcessing when charge completes (success or failure).
  // stripeProcessing is set true on form submit (onSubmitStart) but only reset
  // by onError for Stripe errors. When Stripe succeeds but charge fails,
  // stripeProcessing stays true → button stuck on "Processing..." spinner.
  const prevIsSubmitting = useRef(isSubmitting);
  useEffect(() => {
    if (prevIsSubmitting.current && !isSubmitting && stripeProcessing) {
      setStripeProcessing(false);
    }
    prevIsSubmitting.current = isSubmitting;
  }, [isSubmitting, stripeProcessing]);

  // Matching scio1 isShowConfirmForExpired():
  // Trial without card -> simple "Payment method" (just Stripe form)
  // Paid or has card -> full "Confirmation" with order items + summary + payment
  const isShowConfirm = !isTrial || !!existingCard;
  // Matching scio1's getSubtotalPrice(true): normalScreens * price + addonTotal
  const clientSubtotal = screenCount * currentPricePerScreen + addonTotal;
  // Use backend review when available (total can legitimately be $0 for proration)
  const hasBackendReview = !!reviewResult;
  // Plan edit text matching scio1: "Yearly + 10%" from planEdit.name + planEdit.discount
  const planEditText = planEditName
    ? `${planEditName}${planEditDiscount ? ` + ${planEditDiscount}%` : ''}`
    : BILLING_INTERVAL_LABELS[billingInterval];
  // Plan display for summary: "Pro Plus - Yearly + 10%"
  const planSummaryText = `${getPlanDisplayName(selectedPlan)} - ${planEditText}`;

  // Promo code UI -- matching scio1: "Enter Promo code" link -> label + input (no Apply button)
  // scio1 just stores the value; promo is validated on "Update subscription" click
  // Rendered inline next to email via emailInlineSlot
  const promoCodeUI: ReactNode = isTrial ? (
    showPromoCode ? (
      <div className="w-[200px] space-y-2">
        <Label className="text-sm text-muted-foreground">{t('Promo code')}</Label>
        <Input
          type="text"
          className="w-full uppercase h-[44px]"
          value={promoCode ?? ''}
          onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase() || null)}
        />
      </div>
    ) : (
      <Button
        type="button"
        variant="link"
        size="sm"
        className="text-sm text-primary underline cursor-pointer whitespace-nowrap mb-2 h-auto p-0"
        onClick={onShowPromoCode}
      >
        {t('Enter Promo code')}
      </Button>
    )
  ) : null;

  // Stripe payment form UI (reused)
  const stripeFormUI = (
    <div className="space-y-3">
      {existingCard && (
        <RadioGroup
          value={useExistingCard ? 'existing' : 'new'}
          onValueChange={(v) => onUseExistingCardChange(v === 'existing')}
          className="space-y-2"
        >
          <Label
            htmlFor="payment-existing"
            className={cn(
              'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
              useExistingCard ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50',
            )}
          >
            <RadioGroupItem value="existing" id="payment-existing" className="shrink-0" />
            <span className="text-sm">
              {existingCard.last4
                ? `${existingCard.brand} ****${existingCard.last4}`
                : existingCard.brand}
            </span>
          </Label>
          <Label
            htmlFor="payment-new"
            className={cn(
              'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
              !useExistingCard ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent/50',
            )}
          >
            <RadioGroupItem value="new" id="payment-new" className="shrink-0" />
            <span className="text-sm">{t('New payment method')}</span>
          </Label>
        </RadioGroup>
      )}
      {!useExistingCard && (
        <div className="mt-3">
          {stripeLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : stripeClientSecret ? (
            <StripeCardForm
              clientSecret={stripeClientSecret}
              paymentMethodOrder={stripePaymentMethodOrder}
              returnUrl={stripeReturnUrl}
              defaultEmail={defaultEmail}
              onSuccess={onStripeSuccess}
              onError={(msg) => {
                toast.error(msg);
                setStripeProcessing(false);
              }}
              onSubmitStart={handleSubmitStart}
              submitText={t('Update subscription')}
              showSubmitButton={false}
              isLoading={isSubmitting}
              showBillingAddress
              onBillingAddressChange={onBillingAddressChange}
            />
          ) : (
            <div className="rounded-lg border border-border p-4 text-center text-sm text-muted-foreground">
              {t('Loading payment form...')}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // === TRIAL layout: simple "Payment method" -- just Stripe form ===
  // Matching scio1: Email (with "Enter Promo code" inline) -> Card/US bank tabs -> form fields
  if (!isShowConfirm) {
    return (
      <div className="py-4">
        <h1 className="text-xl font-semibold text-center mb-6">{t('Payment method')}</h1>
        <div className="rounded-lg border p-6">
          {stripeLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : stripeClientSecret ? (
            <StripeCardForm
              clientSecret={stripeClientSecret}
              paymentMethodOrder={stripePaymentMethodOrder}
              returnUrl={stripeReturnUrl}
              defaultEmail={defaultEmail}
              emailInlineSlot={promoCodeUI}
              onSuccess={onStripeSuccess}
              onError={(msg) => {
                toast.error(msg);
                setStripeProcessing(false);
              }}
              onSubmitStart={handleSubmitStart}
              submitText={t('Update subscription')}
              showSubmitButton={false}
              isLoading={isSubmitting}
              showBillingAddress
              onBillingAddressChange={onBillingAddressChange}
            />
          ) : (
            <div className="text-center text-sm text-muted-foreground py-8">
              {t('Loading payment form...')}
            </div>
          )}
        </div>
      </div>
    );
  }

  // === PAID layout: 2-column "Confirmation" matching scio1 (Image #36) ===
  // Left: order items (plan + editable screen count + total)
  // Right: Summary sidebar (billing cycle, plan, subtotal, credit, total, payment method, Update button)
  // Screen change warning text — matching scio1's getTextBucketNormal()
  const bucketText = (() => {
    if (isTrial) return '';
    if (screenCount > originalScreenCount) {
      const added = screenCount - originalScreenCount;
      return `${added} ${t('screens added is allocated to this bucket')}`;
    }
    if (screenCount < originalScreenCount) {
      const reduce = originalScreenCount - screenCount;
      return t('subscriptionPlanModal5', {
        reduce: String(reduce),
        maxQuantityNormalOrigin: String(originalScreenCount),
        getMaxQuantityNormal: String(screenCount),
      });
    }
    return '';
  })();

  // Subtotal = always client-calculated (screens × price), matching scio1's getSubtotalPrice()
  // Credit + Total = from backend getUpcomingSubscriptionReview (prorated amounts)
  const displaySubtotal = clientSubtotal;
  // Credit from getUpcomingSubscription (matching scio1's creditPrice from initFinishPayment)
  // NOT from getUpcomingSubscriptionReview (that's commented out in scio1 line 1804)
  const displayCredit = initialCredit;
  // Total from getUpcomingSubscriptionReview is already in dollars (unlike getUpcomingSubscription which is in cents).
  //
  // Defensive fallback for #32: when the backend review has loaded but returned total = 0
  // for an obviously-should-cost-something scenario, render a placeholder instead of the
  // misleading "$0.00". We deliberately do NOT show clientSubtotal here — it's the full
  // monthly bill, not the prorated charge, and would be wrong by an even larger margin.
  //
  // Trigger condition: backend total is 0 AND clientSubtotal exceeds the available credit.
  // This catches BOTH:
  //   - Screen count increase (1 → 5 screens) — issue #32 original repro
  //   - Plan tier upgrade with same screens (Standard → Pro Plus, 10 screens) — same bug,
  //     different surface, where `screenCount > originalScreenCount` is FALSE
  // It does NOT trigger when credit covers the bill (legitimate $0), and does NOT trigger
  // when clientSubtotal is 0 (no change scenario).
  //
  // Most common cause is a silent Stripe error in open-api's getUpcomingSubscriptionReview
  // catch block — see the matching backend fix in
  // optisigns-open-api/claude-dev/bugfix-anhluu/2026-04-10-fix-confirmation-total-zero/.
  const backendTotal = reviewResult?.total ?? 0;
  const shouldFallback =
    hasBackendReview &&
    backendTotal === 0 &&
    clientSubtotal > 0 &&
    clientSubtotal > Math.abs(initialCredit);
  if (shouldFallback) {
    console.warn(
      'WizardStepConfirmation: getUpcomingSubscriptionReview returned total=0 but clientSubtotal > credit.',
      'Showing "Calculated at checkout" placeholder. Check open-api logs for getUpcomingSubscriptionReview errors.',
      { screenCount, originalScreenCount, clientSubtotal, initialCredit, reviewResult },
    );
  }
  // null sentinel = render the "Calculated at checkout" placeholder; number = render currency
  const displayTotal: number | null = hasBackendReview
    ? shouldFallback
      ? null
      : backendTotal
    : clientSubtotal;

  return (
    <div className="py-4">
      <h1 className="text-xl font-semibold text-center mb-6">{t('Confirmation')}</h1>

      <div
        className={cn(
          'grid gap-4',
          disableScreenCount
            ? 'grid-cols-1 max-w-md mx-auto'
            : 'grid-cols-1 lg:grid-cols-[1fr_320px]',
        )}
      >
        {/* Left -- Order items (hidden in upgrade mode) */}
        {!disableScreenCount && (
          <div className="space-y-4">
            <div className="rounded-lg border p-6">
              <div className="flex items-center gap-6">
                {/* Cart icon + plan info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-semibold">{planEditText}</p>
                  <p className="text-sm text-primary">
                    {formatCurrencyAmount(currentPricePerScreen, currency)} {t('per screen')}
                  </p>
                  {/* Screen change warning — matching scio1 text-warning */}
                  {bucketText && <p className="text-sm text-amber-600 mt-2">{bucketText}</p>}
                </div>

                {/* Editable screen count — hidden in upgrade mode (plan change only) */}
                {!disableScreenCount && (
                  <div className="text-center">
                    <p className="text-sm font-semibold mb-2">{t('Screens')}</p>
                    <div className="flex items-center gap-0">
                      <Button
                        variant="default"
                        size="icon-sm"
                        onClick={() => {
                          if (screenCount > minScreens) onScreenCountChange(screenCount - 1);
                        }}
                        disabled={screenCount <= minScreens}
                        aria-label={t('Decrease')}
                        className={cn(
                          'rounded-r-none shrink-0',
                          screenCount <= minScreens
                            ? 'opacity-50 cursor-not-allowed'
                            : 'cursor-pointer',
                        )}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <ScreenSpinInput
                        value={screenCount}
                        min={minScreens}
                        max={9999}
                        onChange={onScreenCountChange}
                      />
                      <Button
                        variant="default"
                        size="icon-sm"
                        onClick={() => onScreenCountChange(screenCount + 1)}
                        aria-label={t('Increase')}
                        className="rounded-l-none shrink-0 cursor-pointer"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Total — plan only, matching scio1 (addons shown separately in Summary) */}
                <div className="text-right min-w-[100px]">
                  <p className="text-lg font-semibold">
                    {formatCurrencyAmount(screenCount * currentPricePerScreen, currency)}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment method card — matching scio1 showPaymentMethod():
              shown when no card OR when user clicked "(change)" */}
            {(!existingCard || !useExistingCard) && (
              <div className="rounded-lg border p-6 space-y-3">
                <h4 className="text-sm font-semibold">{t('Payment method')}</h4>
                {stripeFormUI}
              </div>
            )}
          </div>
        )}

        {/* Right -- Summary sidebar */}
        <div className="rounded-lg border p-6 space-y-3 self-start">
          <h4 className="text-base font-semibold">{t('Summary')}</h4>

          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Billing Cycle')}</span>
            <span>{planEditText}</span>
          </div>
          <Separator />

          <div className="flex justify-between text-sm">
            <span className="font-semibold">{planSummaryText}</span>
            <span>{formatCurrencyAmount(currentPricePerScreen, currency)}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {screenCount} {t('screens')}
          </p>

          {/* Addon line items — matching scio1's separate addon rows */}
          {addonItems.map((addon) => (
            <div key={addon.key}>
              <Separator />
              <div className="flex justify-between text-sm mt-3">
                <span className="font-semibold">{addon.name}</span>
                <span>{formatCurrencyAmount(addon.pricePerUnit, currency)}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {addon.quantity} {addon.quantity === 1 ? t('screen') : t('screens')}
              </p>
            </div>
          ))}
          <Separator />

          <div className="flex justify-between text-sm">
            <span className="font-semibold">{t('Subtotal')}</span>
            <span>{formatCurrencyAmount(displaySubtotal, currency)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('Credit for previous purchase')}</span>
            <span className="text-primary">
              {reviewLoading ? (
                <Loader2 className="h-3 w-3 animate-spin inline" />
              ) : (
                `${displayCredit < 0 ? '-' : ''}${formatCurrencyAmount(Math.abs(displayCredit), currency)}`
              )}
            </span>
          </div>
          <Separator />

          <div className="flex justify-between text-sm font-semibold">
            <span>{t('Total')}</span>
            <span>
              {reviewLoading ? (
                <Loader2 className="h-3 w-3 animate-spin inline" />
              ) : displayTotal === null ? (
                <span className="text-xs font-normal text-muted-foreground italic">
                  {t('Calculated at checkout')}
                </span>
              ) : (
                formatCurrencyAmount(displayTotal, currency)
              )}
            </span>
          </div>

          {/* Payment method info with (change) link */}
          {existingCard && useExistingCard && (
            <div className="text-center text-xs text-muted-foreground">
              {t('Payment method')} <span className="uppercase">{existingCard.brand}</span>
              {existingCard.last4 ? ` - ${existingCard.last4}` : ''} (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="text-primary cursor-pointer h-auto p-0"
                onClick={() => onUseExistingCardChange(false)}
              >
                {t('change')}
              </Button>
              )
            </div>
          )}

          {/* Update subscription button inside sidebar */}
          <Button
            className="w-full cursor-pointer mt-2"
            onClick={useExistingCard ? onConfirmExistingCard : undefined}
            type={!useExistingCard ? 'submit' : 'button'}
            form={!useExistingCard ? 'stripe-payment-form' : undefined}
            disabled={
              isSubmitting ||
              stripeProcessing ||
              reviewLoading ||
              (!useExistingCard && !stripeClientSecret)
            }
          >
            {isSubmitting || stripeProcessing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {t('Processing...')}
              </>
            ) : (
              t('Update subscription')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
